import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import raceApi from '../../../clients/RaceClient';
import { normalizeLocationKey, LOCATION_API_BASE } from '../../../clients/locationApiBase';
import type { AwaitingGuestPayment, ReleaseHoldResult } from '../../../models/entitlement/RedeemResult';

/**
 * The whole card-hold lifecycle for a member booking with guests: restore a held booking, confirm a
 * card, release a hold ("Change booking"), finish a 3DS return, and the one dangerous state where a
 * card succeeded but the confirm did not. Moved out of EntitlementBooking so the race-nights page
 * shares it instead of forking it. The reasoning comments came with it.
 */

/** A held booking as kept for the tab: the 202, the venue that holds it, and when it was taken. */
type StoredPending = AwaitingGuestPayment & { location: string; heldAtMs?: number };

// A provisional booking already HOLDS its seats, so the card step must survive leaving the page:
// the back button used to drop it, stranding a held booking with no way back to it. Session-scoped
// on purpose — a held booking lapses on the server's hold window, so it must not outlive the tab.
const readPending = (key: string): StoredPending | null => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<StoredPending>;
    // Every field is load-bearing: without all four the card cannot be taken NOR confirmed, and a
    // half-restored state would show a Pay button that can never settle.
    if (!p?.bookingId || !p?.clientSecret || !p?.intentId || typeof p?.amountMinor !== 'number') {
      return null;
    }
    return p as StoredPending;
  } catch {
    return null; // private mode / disabled storage — degrade to no restore
  }
};

const writePending = (key: string, p: StoredPending): void => {
  try { sessionStorage.setItem(key, JSON.stringify(p)); } catch { /* non-fatal */ }
};

const clearPending = (key: string): void => {
  try { sessionStorage.removeItem(key); } catch { /* non-fatal */ }
};

export interface GuestHoldOptions {
  /** sessionStorage key for this page's held booking. Each page has its own. */
  storageKey: string;
  /** Used only for a restored hold stored before holds carried their venue. */
  fallbackLocation: string;
  /** The booking is real: after a card, a recheck, a 3DS return, or a Back that found it paid. */
  onBooked: (reference: string, bookedLocation: string, hasConfirmationCode: boolean) => void | Promise<void>;
  /** "Change booking" released the hold: re-arm the page and reload what it shows. */
  onReleased: () => void;
  /**
   * A restored hold older than this is released instead of offered for payment: its seats may
   * already be sold again (Review Focus 1). Omitted = restore regardless, the pass page's behaviour.
   */
  maxHoldAgeMs?: number;
  /** A restored hold was too old and has been released. */
  onExpired?: () => void;
}

export interface GuestHold {
  pending: AwaitingGuestPayment | null;
  /** The venue that holds the pending booking — the ONLY correct confirm/release target. */
  heldAt: string;
  begin: (result: AwaitingGuestPayment, location: string) => void;
  onCardPaid: (intentId: string) => Promise<void>;
  onBack: () => Promise<string | null>;
  chargedButUnconfirmed: boolean;
  chargedBookingId: string;
  canRecheck: boolean;
  isRechecking: boolean;
  recheck: () => Promise<void>;
}

export function useGuestHold(options: GuestHoldOptions): GuestHold {
  // Latest callbacks without re-running effects: the page re-creates them every render.
  const opts = useRef(options);
  opts.current = options;
  const { storageKey, maxHoldAgeMs } = options;

  const [searchParams] = useSearchParams();
  // Stripe's return_url after a 3DS redirect. Their presence means a card may already have been
  // charged for a booking nothing else will ever confirm.
  const redirectBookingId = searchParams.get('guestBookingId') ?? '';
  const redirectIntentId = searchParams.get('guestIntentId') ?? '';
  // The venue that actually holds the booking. WebApi is deployed PER VENUE, so this is the only
  // correct confirm target on a 3DS return. An absent or unknown value is never replaced with a guess.
  const redirectLocation = normalizeLocationKey(searchParams.get('guestLocation') ?? '');
  const isRedirectReturn = Boolean(redirectBookingId && redirectIntentId);

  const [restored] = useState(() => readPending(storageKey));
  const [restoredIsStale] = useState(() => Boolean(
    maxHoldAgeMs && restored?.heldAtMs && Date.now() - restored.heldAtMs > maxHoldAgeMs));
  // Lazily hydrated so a return to the page lands straight on the card step for a booking that is
  // still being held, rather than an empty page with the seats silently reserved elsewhere.
  const [pending, setPending] = useState<AwaitingGuestPayment | null>(() => (restoredIsStale ? null : restored));
  // The venue that HOLDS the pending booking. On a restored visit the page's own location is derived
  // from the member's PREFERENCE, which need not be where they booked, so the stored venue wins.
  const [pendingLocation, setPendingLocation] = useState<string>(() => (restoredIsStale ? '' : restored?.location ?? ''));
  // When the pending hold was taken, for the hold-window timer. Absent on holds stored before it was kept.
  const [heldAtMs, setHeldAtMs] = useState<number | undefined>(() => (restoredIsStale ? undefined : restored?.heldAtMs));
  // True only in the one dangerous state: the card SUCCEEDED but our confirm call did not. The member
  // has been charged, so nothing may offer a card retry. Only a SUCCESSFUL confirm clears it.
  const [chargedButUnconfirmed, setChargedButUnconfirmed] = useState(false);
  const [chargedIntentId, setChargedIntentId] = useState('');
  const [chargedLocation, setChargedLocation] = useState('');
  const [isRechecking, setIsRechecking] = useState(false);

  const heldAt = pendingLocation || opts.current.fallbackLocation;

  const begin = (result: AwaitingGuestPayment, location: string) => {
    setPending(result);
    // Persist immediately: from here the seats are held, and a navigation away must not lose the
    // only handle on that booking. `location` travels with it because the confirm has to go to the
    // venue that HOLDS the booking, which a fresh mount cannot otherwise know.
    const now = Date.now();
    writePending(storageKey, { ...result, location, heldAtMs: now });
    setPendingLocation(location);
    setHeldAtMs(now);
  };

  /**
   * Runs the server-side confirm for a card that has ALREADY succeeded, and is the only place that
   * decides whether such a booking is real. Every non-success path lands on chargedButUnconfirmed,
   * including a REJECTION: after a succeeded card, a dropped connection means exactly what a non-ok
   * response means — the money moved, the confirmation is unknown.
   */
  const confirmChargedBooking = async (bookingId: string, intentId: string, bookedLocation: string) => {
    // No venue, no call. Confirming against a guessed venue hits a DB with no such booking.
    if (!bookedLocation || !LOCATION_API_BASE[bookedLocation]) {
      setChargedButUnconfirmed(true);
      return;
    }
    let confirmed: Awaited<ReturnType<typeof raceApi.confirmGuestPayment>> = false;
    try {
      confirmed = await raceApi.confirmGuestPayment(bookedLocation, { bookingId, intentId });
    } catch {
      confirmed = false;
    }
    if (confirmed) {
      setChargedButUnconfirmed(false);
      setPending(null);
      // Settled: the hold is gone, so the stored handle must go too.
      clearPending(storageKey);
      // The booking's WOR- code when the confirm sent one; the bare id only as a labelled reference.
      await opts.current.onBooked(
        confirmed.confirmationCode ?? bookingId, bookedLocation, Boolean(confirmed.confirmationCode));
      return;
    }
    setChargedButUnconfirmed(true);
  };

  // The card genuinely succeeded. The server still re-reads the intent from Stripe.
  const onCardPaid = async (intentId: string) => {
    if (!pending) return;
    setChargedIntentId(intentId);
    setChargedLocation(heldAt);
    await confirmChargedBooking(pending.bookingId, intentId, heldAt);
  };

  // Re-runs the confirm ONLY — never the card. The endpoint is idempotent server-side.
  const chargedBookingLocation = isRedirectReturn ? redirectLocation : chargedLocation;
  const chargedBookingId = pending?.bookingId ?? redirectBookingId;
  const canRecheck = Boolean(
    chargedBookingId && chargedIntentId && chargedBookingLocation && LOCATION_API_BASE[chargedBookingLocation],
  );

  const recheck = async () => {
    if (!canRecheck || isRechecking) return;
    setIsRechecking(true);
    try {
      await confirmChargedBooking(chargedBookingId, chargedIntentId, chargedBookingLocation);
    } finally {
      setIsRechecking(false);
    }
  };

  /**
   * "Change booking": release the hold, then hand back to the page. The server re-reads the intent
   * from Stripe before letting go; every outcome that is not a confirmed release keeps the card step
   * or moves FORWARD — a member who may have been charged is never sent back to re-book.
   */
  const onBack = async (): Promise<string | null> => {
    if (!pending) return null;
    let result: ReleaseHoldResult;
    try {
      result = await raceApi.releaseGuestHold(heldAt, { bookingId: pending.bookingId, intentId: pending.intentId });
    } catch {
      result = { outcome: 'failed' };
    }
    if (result.outcome === 'released') {
      clearPending(storageKey);
      setPending(null);
      setPendingLocation('');
      opts.current.onReleased();
      return null;
    }
    if (result.outcome === 'alreadyPaid') {
      // The card went through after all. Confirm it, never abandon it.
      setChargedIntentId(pending.intentId);
      setChargedLocation(heldAt);
      await confirmChargedBooking(pending.bookingId, pending.intentId, heldAt);
      return null;
    }
    if (result.outcome === 'processing') return result.error;
    return 'Couldn’t go back just now. Please try again.';
  };

  // Landing back from a 3DS redirect: Stripe took the card on another page, so this mount is the ONLY
  // thing that will ever call confirm for that booking. Ref-guarded so it fires once.
  const redirectConfirmed = useRef(false);
  useEffect(() => {
    if (!isRedirectReturn || redirectConfirmed.current) return;
    redirectConfirmed.current = true;
    setChargedIntentId(redirectIntentId);
    setChargedLocation(redirectLocation);
    void confirmChargedBooking(redirectBookingId, redirectIntentId, redirectLocation);
    // confirmChargedBooking is re-created each render; the ref guard is what enforces once-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRedirectReturn, redirectBookingId, redirectIntentId, redirectLocation]);

  // Review Focus 1: a hold past its window is let go, not paid for. Release voids the intent first on
  // the server; a card that cleared in the meantime comes back alreadyPaid and is confirmed. Any other
  // outcome still drops the Pay button: the server no longer counts the seat, so it may be sold again.
  const expireHold = (held: AwaitingGuestPayment, at: string) =>
    raceApi.releaseGuestHold(at, { bookingId: held.bookingId, intentId: held.intentId })
      .then((r) => {
        if (r.outcome === 'alreadyPaid') {
          setChargedIntentId(held.intentId);
          setChargedLocation(at);
          return confirmChargedBooking(held.bookingId, held.intentId, at);
        }
        clearPending(storageKey);
        setPending(null);
        setPendingLocation('');
        opts.current.onExpired?.();
        return undefined;
      })
      .catch(() => {
        clearPending(storageKey);
        setPending(null);
        setPendingLocation('');
        opts.current.onExpired?.();
      });

  // A restored hold that is already too old.
  const staleHandled = useRef(false);
  useEffect(() => {
    if (!restoredIsStale || !restored || staleHandled.current) return;
    staleHandled.current = true;
    clearPending(storageKey);
    void expireHold(restored, restored.location || opts.current.fallbackLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A hold that runs out while the page is still open (the tab was simply left on the card step).
  // Not once the card has been charged: that booking is confirmed, never released.
  useEffect(() => {
    if (!maxHoldAgeMs || !pending || !heldAtMs || chargedButUnconfirmed) return undefined;
    const held = pending;
    const at = heldAt;
    const timer = setTimeout(() => { void expireHold(held, at); }, Math.max(0, heldAtMs + maxHoldAgeMs - Date.now()));
    return () => clearTimeout(timer);
    // expireHold is re-created each render; the hold it acts on is captured above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, heldAtMs, maxHoldAgeMs, chargedButUnconfirmed, heldAt]);

  return {
    pending, heldAt, begin, onCardPaid, onBack,
    chargedButUnconfirmed, chargedBookingId, canRecheck, isRechecking, recheck,
  };
}
